/**
 * Data utility functions for LinCal
 * Handles filtering, color inheritance, and data transformations
 */

/**
 * Filter items by project visibility
 * @param {Array} items - Array of items (events or milestones)
 * @param {Array} projects - Array of project objects
 * @param {Function} getProjectId - Function to extract projectId from item (default: item => item.projectId)
 * @returns {Array} Filtered array of items from visible projects
 */
export function filterByProjectVisibility(items, projects, getProjectId = item => item.projectId) {
  return items.filter(item => {
    const projectId = getProjectId(item);
    if (!projectId) return true;
    const project = projects.find(p => p.id === projectId);
    return project && project.visible;
  });
}

/**
 * Get the effective color for an entity (own color or inherited from project)
 * @param {Object} entity - Event or milestone object
 * @param {Array} projects - Array of project objects
 * @returns {string|null} Hex color string or null if not found
 */
export function resolveEntityColor(entity, projects) {
  // If entity has its own color, use it
  if (entity.color) {
    return entity.color;
  }
  
  // Otherwise, try to inherit from project
  if (entity.projectId) {
    const project = projects.find(p => p.id === entity.projectId);
    if (project) {
      return project.color;
    }
  }
  
  return null;
}

/**
 * Return entity with color property resolved (for display)
 * If entity has no color, inherits from project
 * @param {Object} entity - Event or milestone object
 * @param {Array} projects - Array of project objects
 * @returns {Object} Entity object with guaranteed color property
 */
export function applyColorInheritance(entity, projects) {
  // If entity already has a color, return as-is
  if (entity.color) {
    return entity;
  }
  
  // Try to inherit from project
  const resolvedColor = resolveEntityColor(entity, projects);
  if (resolvedColor) {
    return { ...entity, color: resolvedColor };
  }
  
  return entity;
}

/**
 * Apply color inheritance to an array of entities
 * @param {Array} entities - Array of events or milestones
 * @param {Array} projects - Array of project objects
 * @returns {Array} Array of entities with resolved colors
 */
export function applyColorInheritanceToList(entities, projects) {
  return entities.map(entity => applyColorInheritance(entity, projects));
}
